/**
 * The native-shell half of WagePro admin sign-in: what to SEND on the
 * signup/login mutation, and what to do with what comes back.
 *
 * Pure and dependency-injected — no Capacitor import, no `lib/trpc.ts`
 * import — so both halves are unit-testable in `bun:test` without a bridge.
 * The screens supply the runtime facts (`isNative()`) and the persistence
 * function (`setNativeSessionToken`).
 *
 * Background: the shell's WebView origin and the API are on different
 * registrable domains, so the `SameSite=Lax` session cookie is dropped on
 * every cross-site request. The shell opts into body-carried transport
 * instead. See `docs/adr/20260908_01_wagepro-native-shell-session-bearer.md`
 * and `apps/api/src/trpc/routers/wagepro_auth.ts`'s module docblock.
 */

/**
 * The `client` discriminator the API's `wageproAuth.signup`/`login` schemas
 * accept (`z.literal`, so anything else is a `BAD_REQUEST`). Must stay
 * byte-identical to `NATIVE_SHELL_CLIENT` in
 * `apps/api/src/trpc/routers/wagepro_auth.ts` — pinned across both files by
 * `apps/api/src/wagepro_shell_origin.test.ts`'s
 * "WagePro shell — session transport" block, because a silent drift here
 * fails only in a shipped native build (the web console never sends it) and
 * is invisible to TypeScript on the wagepro-web side.
 */
export const NATIVE_SHELL_CLIENT = 'native-shell';

/**
 * The `client` field to spread into a signup/login mutation input — the
 * literal on native, and NOTHING on web.
 *
 * Spread rather than a conditional value so the web request body is
 * byte-identical to what it was before this change: the key is absent, not
 * present-and-`undefined`.
 */
export function nativeClientInput(native: boolean): {
  client?: typeof NATIVE_SHELL_CLIENT;
} {
  return native ? { client: NATIVE_SHELL_CLIENT } : {};
}

/** The part of a `wageproAuth.signup`/`login` response this module reads. */
export interface NativeSessionCarrier {
  native_session_token?: string;
}

/** Injected collaborators, so {@link persistNativeSession} needs no Capacitor bridge under test. */
export interface PersistNativeSessionDeps {
  /** Whether this is the native Capacitor runtime. */
  native: boolean;
  /** Persistence — `native_session_store.ts`'s `setNativeSessionToken` in production. */
  setToken: (token: string) => Promise<void>;
}

/**
 * Persist the session id a native sign-in returned. Resolves to whether the
 * caller may proceed into the app.
 *
 * - **Web** (`native === false`): always `true`, and nothing is stored. The
 *   httpOnly cookie already is the session; this function is inert.
 * - **Native, token present**: stores it, returns `true`.
 * - **Native, token ABSENT**: returns `false`.
 *
 * That last case is the one this function exists for. It is reachable in
 * production through pure release ordering: a shipped shell that sends
 * `client: 'native-shell'` against an API deployed BEFORE this change gets a
 * successful 200 with no `native_session_token` in it. Navigating into the
 * app anyway would land the user in the exact 401 boot loop this whole
 * change removes — an authenticated-looking blank app that bounces to
 * `/login` — with no error message anywhere. Returning `false` lets the
 * screen keep its existing error state and tell the user sign-in failed,
 * which is both true and actionable.
 *
 * A rejection from `setToken` is deliberately NOT swallowed: a token we
 * failed to store is a token we cannot present on the next request, so the
 * caller must treat it as a failed sign-in rather than navigate.
 */
export async function persistNativeSession(
  result: NativeSessionCarrier,
  deps: PersistNativeSessionDeps,
): Promise<boolean> {
  if (!deps.native) return true;
  const token = result.native_session_token;
  if (typeof token !== 'string' || token === '') return false;
  await deps.setToken(token);
  return true;
}
